// =============================================================================
// File: frontend/src/components/ConfigurationGuides.jsx
// Configuration Tips & Guides Page
// =============================================================================
//
// Sidebar menu item under "Overview" section
// Contains:
// - PWA installation instructions
// - Apple Watch bridge app setup
// - Device connection guides
// - Notification setup
// =============================================================================

import React, { useState } from 'react';

const ConfigurationGuides = ({ fetchWithAuth, API_BASE, token }) => {
  const [expandedSection, setExpandedSection] = useState('pwa');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // ==========================================================================
  // SECTION: PWA INSTALLATION
  // ==========================================================================
  
  const PWASection = () => {
    const [platform, setPlatform] = useState('ios');
    
    return (
      <div className="config-section-content">
        <p className="config-paragraph">
          Installing Healist on your device enables push notifications for health reminders, 
          faster loading, and offline access. It only takes a minute!
        </p>

        <div className="config-platform-tabs">
          <button 
            className={`config-platform-tab ${platform === 'ios' ? 'active' : ''}`}
            onClick={() => setPlatform('ios')}
          >
            iPhone / iPad
          </button>
          <button 
            className={`config-platform-tab ${platform === 'android' ? 'active' : ''}`}
            onClick={() => setPlatform('android')}
          >
            Android
          </button>
        </div>

        {platform === 'ios' && (
          <>
            <div className="config-info-box">
              <div className="config-info-box-title">
                <span></span> Why Install?
              </div>
              <p className="config-info-box-text">
                On iOS, push notifications only work when Healist is installed as an app on your home screen. 
                This is an Apple requirement for web apps.
              </p>
            </div>

            <ol className="config-step-list">
              <li className="config-step">
                <span className="config-step-number">1</span>
                <span className="config-step-content">
                  Open <span className="config-highlight">healist.ai</span> in Safari (must be Safari, not Chrome)
                </span>
              </li>
              <li className="config-step">
                <span className="config-step-number">2</span>
                <span className="config-step-content">
                  Tap the <span className="config-highlight">Share</span> button <span className="config-share-icon">⎙</span> at the bottom of the screen
                </span>
              </li>
              <li className="config-step">
                <span className="config-step-number">3</span>
                <span className="config-step-content">
                  Scroll down in the share menu and tap <span className="config-highlight">Add to Home Screen</span>
                </span>
              </li>
              <li className="config-step">
                <span className="config-step-number">4</span>
                <span className="config-step-content">
                  Tap <span className="config-highlight">Add</span> in the top right corner
                </span>
              </li>
              <li className="config-step">
                <span className="config-step-number">5</span>
                <span className="config-step-content">
                  Open Healist from your home screen and enable notifications when prompted!
                </span>
              </li>
            </ol>
          </>
        )}

        {platform === 'android' && (
          <ol className="config-step-list">
            <li className="config-step">
              <span className="config-step-number">1</span>
              <span className="config-step-content">
                Open <span className="config-highlight">healist.ai</span> in Chrome
              </span>
            </li>
            <li className="config-step">
              <span className="config-step-number">2</span>
              <span className="config-step-content">
                Tap the <span className="config-highlight">⋮ Menu</span> button (three dots) in the top right
              </span>
            </li>
            <li className="config-step">
              <span className="config-step-number">3</span>
              <span className="config-step-content">
                Tap <span className="config-highlight">Install app</span> or <span className="config-highlight">Add to Home screen</span>
              </span>
            </li>
            <li className="config-step">
              <span className="config-step-number">4</span>
              <span className="config-step-content">
                Tap <span className="config-highlight">Install</span> to confirm, then enable notifications when prompted
              </span>
            </li>
          </ol>
        )}
      </div>
    );
  };

  // ==========================================================================
  // SECTION: APPLE WATCH BRIDGE APP
  // ==========================================================================
  
  const AppleWatchSection = () => (
    <div className="config-section-content">
      <p className="config-paragraph">
        HealistHealthSync connects your Apple Health data with your Healist account.
        It runs in the background and automatically syncs 191 health data types including activity, heart rate, sleep, clinical records, and more.
      </p>

      <div className="config-info-box">
        <div className="config-info-box-title">
          <span></span> How It Works
        </div>
        <p className="config-info-box-text">
          Apple Health data can only be accessed by native iOS apps (not websites). HealistHealthSync
          reads your Apple Health data and securely sends it to your Healist account. Your data
          is encrypted in transit and at rest. All wearable and medical device data flows through
          Apple Health as the single intermediary.
        </p>
      </div>

      <h4 className="config-section-subtitle">Installation Steps</h4>

      <ol className="config-step-list">
        <li className="config-step">
          <span className="config-step-number">1</span>
          <span className="config-step-content">
            Download <span className="config-highlight">HealistHealthSync</span> from the App Store (link below)
          </span>
        </li>
        <li className="config-step">
          <span className="config-step-number">2</span>
          <span className="config-step-content">
            Open the app and tap <span className="config-highlight">Sign In</span> to connect your Healist account
          </span>
        </li>
        <li className="config-step">
          <span className="config-step-number">3</span>
          <span className="config-step-content">
            Grant Apple Health permissions when prompted. The app requests access to 191 data types
            including activity, body measurements, heart, nutrition, respiratory, sleep, and vitals.
          </span>
        </li>
        <li className="config-step">
          <span className="config-step-number">4</span>
          <span className="config-step-content">
            Enable <span className="config-highlight">Background App Refresh</span> in Settings &rarr; HealistHealthSync
            for automatic syncing
          </span>
        </li>
        <li className="config-step">
          <span className="config-step-number">5</span>
          <span className="config-step-content">
            Your data will appear in Healist within minutes. The app syncs approximately every 2 hours
            in the background.
          </span>
        </li>
      </ol>

      <a
        href="https://apps.apple.com/app/healisthealthsync/id6743597614"
        target="_blank"
        rel="noopener noreferrer"
        className="config-app-store-link"
      >
        <img
          src="/images/app-store-badge.svg"
          alt="Download on the App Store"
          className="config-app-store-img"
        />
      </a>

      <div className="config-warning-box">
        <div className="config-warning-title">
          <span></span> Important
        </div>
        <p className="config-info-box-text">
          HealistHealthSync must remain installed for continuous syncing. If you delete it,
          Apple Health data will stop syncing until you reinstall.
        </p>
      </div>

      <h4 className="config-section-subtitle">Data Categories Synced (191 Types)</h4>
      <p className="config-paragraph">
        ✓ Activity &nbsp;|&nbsp; ✓ Body Measurements &nbsp;|&nbsp; ✓ Heart &nbsp;|&nbsp;
        ✓ Nutrition &nbsp;|&nbsp; ✓ Reproductive Health &nbsp;|&nbsp; ✓ Respiratory &nbsp;|&nbsp;
        ✓ Sleep &nbsp;|&nbsp; ✓ Vitals &nbsp;|&nbsp; ✓ Clinical Records (Labs, Conditions, Medications, Allergies)
      </p>
    </div>
  );

  // ==========================================================================
  // SECTION: NOTIFICATION SETUP
  // ==========================================================================
  
  const NotificationSection = () => (
    <div className="config-section-content">
      <p className="config-paragraph">
        Healist can send you reminders for hydration, meals, medication, and achievements. 
        Push notifications are completely free - no SMS costs!
      </p>

      <h4 className="config-section-subtitle">Available Notifications</h4>
      
      <div className="config-notification-grid">
        {[
          {icon: '', name: 'Hydration Reminders', desc: 'Customizable daily water reminders'},
          {icon: '', name: 'Meal Alerts', desc: 'When it\'s time to prepare your planned meals'},
          {icon: '', name: 'Medication Reminders', desc: 'Never miss a dose'},
          {icon: '', name: 'Goal Achievements', desc: 'Celebrate hitting your targets'},
          {icon: '', name: 'Weekly Summary', desc: 'Your health report every Sunday'},
          {icon: '', name: 'Pantry Alerts', desc: 'When items are expiring soon'}
        ].map(item => (
          <div key={item.name} className="config-notification-item">
            <span className="config-notification-icon">{item.icon}</span>
            <div>
              <div className="config-notification-name">{item.name}</div>
              <div className="config-notification-desc">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="config-info-box">
        <div className="config-info-box-title">
          <span></span> Customize
        </div>
        <p className="config-info-box-text">
          Go to <strong>Settings → Notifications</strong> tab to customize which notifications
          you receive, set quiet hours, and send test notifications.
        </p>
      </div>
    </div>
  );

  // ==========================================================================
  // SECTION: DEVICE CONNECTIONS
  // ==========================================================================
  
  const DeviceSection = () => (
    <div className="config-section-content">
      <p className="config-paragraph">
        All device data flows through your mobile health platform &mdash; Apple Health (iOS) or
        Google Health Connect (Android). Any device that writes to these platforms is automatically
        supported by Healist.
      </p>

      <div className="config-info-box">
        <div className="config-info-box-title">
          <span></span> Compatible Devices
        </div>
        <p className="config-info-box-text">
          Apple Watch, Fitbit, Garmin, WHOOP, Oura Ring, Withings, Dexcom CGM, and hundreds more.
          If your device syncs to Apple Health or Google Health Connect, it works with Healist.
        </p>
      </div>

      <div className="config-device-grid">
        {[
          {name: 'iOS Devices', icon: '', status: 'Via HealistHealthSync', available: true, note: 'Install HealistHealthSync from the App Store to sync Apple Health data'},
          {name: 'Android Devices', icon: '', status: 'Coming Soon', available: false, note: 'HealistHealthSync for Android will sync Google Health Connect data'},
        ].map(device => (
          <div key={device.name} className="config-device-item">
            <span className="config-device-icon">{device.icon}</span>
            <div className="config-device-info">
              <div className="config-device-name">{device.name}</div>
              <div className="config-device-note">{device.note}</div>
            </div>
            <div className={`config-device-status ${device.available ? 'available' : 'coming-soon'}`}>
              {device.status}
            </div>
          </div>
        ))}
      </div>

      <button
        className="config-button"
        onClick={() => window.location.hash = '#devices'}
      >
        View Connected Devices
      </button>
    </div>
  );

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const sections = [
    { id: 'pwa', icon: '', title: 'Install Healist App', component: PWASection },
    { id: 'watch', icon: '', title: 'Apple Watch & Health Setup', component: AppleWatchSection },
    { id: 'notifications', icon: '', title: 'Notification Settings', component: NotificationSection },
    { id: 'devices', icon: '', title: 'Connect Devices', component: DeviceSection }
  ];

  return (
    <div className="config-guides-container">
      <div className="config-guides-header">
        <h1 className="config-guides-title">Configuration Tips & Guides</h1>
        <p className="config-guides-subtitle">Get the most out of Healist with these setup guides</p>
      </div>

      {sections.map(section => (
        <div key={section.id} className="config-section">
          <div 
            className={`config-section-header ${expandedSection === section.id ? 'active' : ''}`}
            onClick={() => toggleSection(section.id)}
          >
            <div className="config-section-title">
              <span className="config-section-icon">{section.icon}</span>
              {section.title}
            </div>
            <span className={`config-section-chevron ${expandedSection === section.id ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {expandedSection === section.id && <section.component />}
        </div>
      ))}
    </div>
  );
};

export default ConfigurationGuides;